import React from 'react';
import { Link } from 'react-router-dom';
import Reveal from '../common/Reveal';
import SectionHead from '../common/SectionHead';
import StoreButtons from '../common/StoreButtons';
import PhoneMockup from '../common/PhoneMockup';
import { useLang } from '../../i18n/LanguageContext';

export const PARTNER_LINK = '/contact?topic=partnership';

// Product status notice — keep visible wherever the product is described.
export function StatusNote() {
  const { t } = useLang();
  return (
    <p className="status-note">
      <span className="status-note__label">
        <span className="status-note__dot" aria-hidden="true" />
        {t.status.label}
      </span>
      <span>{t.status.text}</span>
    </p>
  );
}

export function Hero() {
  const { t } = useLang();
  const h = t.hero;
  return (
    <section className="hero">
      <div className="hero-grid" aria-hidden="true" />
      <div className="container hero__inner">
        <div className="hero__copy">
          <Reveal as="p" className="badge">
            <i className="fa-solid fa-shield-halved" aria-hidden="true" /> {h.eyebrow}
          </Reveal>
          <Reveal as="h1" delay={80} className="hero__title">
            {h.title} <span className="hero__accent">{h.titleAccent}</span>
          </Reveal>
          <Reveal as="p" delay={160} className="hero__lead">
            {h.lead}
          </Reveal>
          <Reveal as="ul" delay={220} className="hero__pillars">
            {h.pillars.map((x, i) => (
              <li key={x}>
                <span className="hero__pillar-num">{i + 1}</span> {x}
              </li>
            ))}
          </Reveal>
          <Reveal delay={280} className="hero__ctas">
            <Link to="/safehand" className="btn btn--primary btn--lg">
              {h.primary} <i className="fa-solid fa-arrow-right flip-rtl" aria-hidden="true" />
            </Link>
            <Link to={PARTNER_LINK} className="btn btn--outline btn--lg">
              <i className="fa-solid fa-handshake" aria-hidden="true" /> {h.secondary}
            </Link>
          </Reveal>
          <Reveal delay={340}>
            <StatusNote />
          </Reveal>
        </div>
        <Reveal delay={200} className="hero__visual">
          <PhoneMockup />
        </Reveal>
      </div>
    </section>
  );
}

export function Problem() {
  const { t } = useLang();
  const p = t.problem;
  return (
    <section className="section">
      <div className="container">
        <SectionHead eyebrow={p.eyebrow} title={p.title} lead={p.lead} />
        <div className="grid grid--3">
          {p.items.map((it, i) => (
            <Reveal key={it.title} delay={i * 90} className="card card--problem">
              <span className="icon-tile icon-tile--warn">
                <i className={it.icon} aria-hidden="true" />
              </span>
              <h3 className="card__title">{it.title}</h3>
              <p className="card__text">{it.text}</p>
            </Reveal>
          ))}
        </div>
        <Reveal className="answer">
          <span className="answer__icon">
            <i className="fa-solid fa-diagram-project" aria-hidden="true" />
          </span>
          <div>
            <h3>{p.answerTitle}</h3>
            <p>{p.answerText}</p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

// "A payment vs a transaction" comparison
export function Difference() {
  const { t } = useLang();
  const d = t.diff;
  return (
    <section className="section section--tint">
      <div className="container">
        <SectionHead eyebrow={d.eyebrow} title={d.title} lead={d.lead} />
        <div className="compare">
          <Reveal className="compare__col compare__col--muted">
            <h3>
              <i className="fa-solid fa-money-bill-transfer" aria-hidden="true" /> {d.leftTitle}
            </h3>
            <ul>
              {d.left.map((x) => (
                <li key={x}>
                  <i className="fa-solid fa-minus" aria-hidden="true" /> {x}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={120} className="compare__col compare__col--brand">
            <h3>
              <i className="fa-solid fa-diagram-project" aria-hidden="true" /> {d.rightTitle}
            </h3>
            <ul>
              {d.right.map((x) => (
                <li key={x}>
                  <i className="fa-solid fa-check" aria-hidden="true" /> {x}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

export function HowItWorks() {
  const { t } = useLang();
  const h = t.how;
  return (
    <section className="section" id="how">
      <div className="container">
        <SectionHead eyebrow={h.eyebrow} title={h.title} lead={h.lead} />
        <ol className="steps">
          {h.steps.map((s, i) => (
            <Reveal as="li" key={s.title} delay={i * 90} className="step">
              <span className="step__num">{String(i + 1).padStart(2, '0')}</span>
              <span className="icon-tile">
                <i className={s.icon} aria-hidden="true" />
              </span>
              <h3 className="card__title">{s.title}</h3>
              <p className="card__text">{s.text}</p>
            </Reveal>
          ))}
        </ol>
        <Reveal className="dispute">
          <i className="fa-solid fa-circle-info" aria-hidden="true" />
          <p>
            <strong>{h.noteTitle}</strong> {h.noteText}
          </p>
        </Reveal>
      </div>
    </section>
  );
}

export function Features() {
  const { t } = useLang();
  const f = t.features;
  return (
    <section className="section section--dark">
      <div className="hero-grid" aria-hidden="true" />
      <div className="container">
        <SectionHead eyebrow={f.eyebrow} title={f.title} lead={f.lead} />
        <div className="grid grid--3">
          {f.items.map((it, i) => (
            <Reveal key={it.title} delay={(i % 3) * 80} className="feature">
              <span className="feature__icon">
                <i className={it.icon} aria-hidden="true" />
              </span>
              <div>
                <h3 className="feature__title">{it.title}</h3>
                <p className="feature__text">{it.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

// Who the platform is for — each card has an appropriate next step
export function Audiences() {
  const { t } = useLang();
  const a = t.audiences;
  return (
    <section className="section">
      <div className="container">
        <SectionHead eyebrow={a.eyebrow} title={a.title} />
        <div className="grid grid--4">
          {a.items.map((it, i) => {
            const isPartner = it.link === 'partner';
            return (
              <Reveal key={it.title} delay={i * 80} className="card card--use card--link">
                <span className="icon-tile icon-tile--soft">
                  <i className={it.icon} aria-hidden="true" />
                </span>
                <h3 className="card__title">{it.title}</h3>
                <p className="card__text">{it.text}</p>
                <Link to={isPartner ? PARTNER_LINK : '/safehand#how'} className="text-link card__cta">
                  {isPartner ? t.common.partner : t.nav.how}{' '}
                  <i className="fa-solid fa-arrow-right flip-rtl" aria-hidden="true" />
                </Link>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export function BuyersSellers() {
  const { t } = useLang();
  const a = t.buyersSellers;
  return (
    <section className="section section--tint">
      <div className="container grid grid--2">
        {[a.buyers, a.sellers].map((g, i) => (
          <Reveal key={g.title} delay={i * 100} className={`audience ${i ? 'audience--alt' : ''}`}>
            <h3>
              <i className={`fa-solid ${i ? 'fa-store' : 'fa-bag-shopping'}`} aria-hidden="true" /> {g.title}
            </h3>
            <ul className="checklist">
              {g.points.map((p) => (
                <li key={p}>
                  <i className="fa-solid fa-check" aria-hidden="true" /> {p}
                </li>
              ))}
            </ul>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

// Two-layer diagram: transaction workflow (SafeHand) vs custody of funds (licensed institutions)
export function OperatingModel() {
  const { t } = useLang();
  const m = t.model;
  return (
    <section className="section section--tint" id="partners">
      <div className="container">
        <SectionHead eyebrow={m.eyebrow} title={m.title} lead={m.lead} />
        <Reveal className="layers">
          <div className="layer layer--app">
            <div className="layer__head">
              <span className="layer__tag">{m.layer1Tag}</span>
              <h3>{m.layer1Title}</h3>
            </div>
            <ul className="layer__items">
              {m.layer1.map((x) => (
                <li key={x}>{x}</li>
              ))}
            </ul>
          </div>
          <div className="layers__link" aria-hidden="true">
            <i className="fa-solid fa-arrows-up-down" />
          </div>
          <div className="layer layer--fin">
            <div className="layer__head">
              <span className="layer__tag">{m.layer2Tag}</span>
              <h3>{m.layer2Title}</h3>
            </div>
            <ul className="layer__items">
              {m.layer2.map((x) => (
                <li key={x}>{x}</li>
              ))}
            </ul>
          </div>
        </Reveal>
        <Reveal className="model-note">
          <i className="fa-solid fa-building-columns" aria-hidden="true" />
          <p>{m.note}</p>
        </Reveal>
        <Reveal className="model-cta">
          <div>
            <h3>{m.ctaTitle}</h3>
            <p>{m.ctaText}</p>
          </div>
          <Link to={PARTNER_LINK} className="btn btn--primary">
            <i className="fa-solid fa-handshake" aria-hidden="true" /> {t.common.partner}
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

export function PartnerBand() {
  const { t } = useLang();
  return (
    <section className="section section--tight">
      <div className="container">
        <Reveal className="cta-band">
          <div className="cta-band__copy">
            <h2>{t.partnerBand.title}</h2>
            <p>{t.partnerBand.text}</p>
          </div>
          <div className="cta-band__actions">
            <Link to={PARTNER_LINK} className="btn btn--primary btn--lg">
              {t.common.partner} <i className="fa-solid fa-arrow-right flip-rtl" aria-hidden="true" />
            </Link>
            <Link to="/faq" className="btn btn--outline btn--lg">
              {t.nav.faq}
            </Link>
          </div>
          {/* Renders only when real store links are configured */}
          <StoreButtons variant="dark" />
        </Reveal>
      </div>
    </section>
  );
}
